/**
 * WelcomeNudge — the one-time dismissible welcome banner on the inviting
 * org's About page (onboarding chunk A; SCOPING_ONBOARDING_FLOW §7.9).
 *
 * Renders ONLY for an org-invited Explorer, on THEIR inviting org's page,
 * until dismissed. It points at the Action Center, whose "Getting started"
 * checklist IS the acclimation tour — no wizard (§7.9: wizard is CUT).
 * Dismissal persists SERVER-SIDE on the onboarding profile via the
 * welcome_nudge_dismissed flag (§7.9 r4 — never localStorage), so it
 * survives reloads and devices.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiOnboarding } from "../../api/useApiOnboarding";
import { useOnboardingCopy, resolveCopyOverride } from "../../api/onboardingCopy";
import i18n from "../../i18n/config";
import styles from "./WelcomeNudge.module.css";

interface Props {
  communityGuid: string;
  communityName: string;
  currentUserGuid?: string | null;
}

const WelcomeNudge: React.FC<Props> = ({
  communityGuid,
  communityName,
  currentUserGuid,
}) => {
  const { t } = useAppTranslation("pages");
  const navigate = useNavigate();
  const {
    archetype,
    source,
    invitedCommunityGuid,
    achievedMilestones,
    recordMilestone,
    loading,
  } = useApiOnboarding(currentUserGuid || null);

  // Optimistic local hide while the server write lands.
  const [dismissedLocally, setDismissedLocally] = useState(false);

  const isMyInvitingOrg =
    !!invitedCommunityGuid &&
    String(invitedCommunityGuid).toUpperCase() === String(communityGuid).toUpperCase();

  const show =
    !loading &&
    !dismissedLocally &&
    source === "invited" &&
    archetype === "explorer" &&
    isMyInvitingOrg &&
    !achievedMilestones.has("welcome_nudge_dismissed");

  // Admin copy overrides (onboarding_copy) — DB override → static i18n.
  const { copyMap } = useOnboardingCopy(show);
  const lang = i18n.language || "en";
  const copy = (shortKey: string, vars?: Record<string, any>): string =>
    resolveCopyOverride(copyMap, lang, `pages:${shortKey}`, vars) ??
    t(shortKey, vars as never);

  if (!show) return null;

  const dismiss = () => {
    setDismissedLocally(true);
    recordMilestone({ milestoneKey: "welcome_nudge_dismissed" }).catch(() => {
      /* best-effort; the flag re-offers on next load if the write failed */
    });
  };

  return (
    <div className={styles.nudge} role="status">
      <button
        type="button"
        className={styles.close}
        aria-label={t("welcomeNudge.dismiss")}
        title={t("welcomeNudge.dismiss")}
        onClick={dismiss}
      >
        ×
      </button>
      <div className={styles.title}>
        {copy("welcomeNudge.title", { org: communityName })}
      </div>
      <p className={styles.body}>{copy("welcomeNudge.body")}</p>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.cta}
          onClick={() => {
            // Dismissing on follow-through too — the nudge's one job is done.
            dismiss();
            navigate("/actions");
          }}
        >
          {copy("welcomeNudge.cta")}
        </button>
      </div>
    </div>
  );
};

export default WelcomeNudge;
